import { useState, useCallback } from 'react'

import { useGetLandlordRefereeQuery } from '@/lib/apollo'
import type { LandlordReferee } from '@/lib/apollo/query/get-landlord-referee'
import QUERIES from '@/lib/graphql'
import type { Application } from '@/types/Activity'

import { graphQLQuery } from './hooks'

export type FetchSingleApplication = (
  customerId: string,
  subjectId: string,
) => Promise<void>

const useLandlordReferee = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [landlordReferee, setLandlordReferee] = useState<LandlordReferee | undefined>(
    undefined,
  )
  const [application, setApplication] = useState<Application | undefined>(undefined)

  const [fetchLandlordReferee] = useGetLandlordRefereeQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  })

  const fetchSingleApplication = useCallback<FetchSingleApplication>(
    async (customerId: string, subjectId: string) => {
      setLoading(true)
      setApplication(undefined)
      setLandlordReferee(undefined)
      setError(undefined)

      try {
        const { data: landlordRefereeData } = await fetchLandlordReferee({
          variables: {
            customerId,
            subjectId,
          },
        })

        if (!landlordRefereeData) {
          throw Error('No subject was retrieved')
        }

        setLandlordReferee(landlordRefereeData?.getLandlordReferee)

        const getSingleApplicationResponse = await graphQLQuery({
          query: QUERIES.getSingleApplication,
          variables: {
            customerId,
            applicationId: landlordRefereeData?.getLandlordReferee.applicationId,
            subjectId,
          },
          endpoint: `${import.meta.env.VITE_REPORTS_GRAPHQL}`,
        })

        const data = await getSingleApplicationResponse.json()

        if (!data || !data.data) {
          throw Error('Get single application returned undefined')
        }

        setApplication(data.data.getDashboardApplication)
      } catch (e) {
        console.error(e)
        setError('Failed to retrieve applicant')
      } finally {
        setLoading(false)
      }
    },
    [fetchLandlordReferee, setLoading, setError, setApplication],
  )

  return {
    loading,
    error,
    landlordReferee,
    application,
    fetchSingleApplication,
  }
}

export default useLandlordReferee
